import { useId, useMemo, useState } from "react";
import { Text } from "@blycare/polar-ui";
import { useTranslation } from "react-i18next";
import { isIsoDate, isValidDateRange, type DateRange } from "@/utils/date/date-range";
import { resolveTimeZone, todayIso, type DateTimeZone } from "@/utils/date/time-zone";
import {
  DATE_PRESETS,
  calendarCells,
  calendarDayRole,
  clampDateRange,
  isDateAfterToday,
  matchPreset,
  monthKey,
  pickerViewMonth,
  rangeForPreset,
  resolvePickerRange,
  shiftMonth,
  type DatePreset,
} from "./utils";

export function DateRangeFilter({
  value,
  onApply,
  timeZone,
}: {
  value: DateRange;
  onApply: (range: DateRange) => void;
  timeZone?: DateTimeZone;
}) {
  const { t } = useTranslation();
  const popoverId = useId().replace(/:/g, "");
  const modalId = useId().replace(/:/g, "");
  const picker = usePickerState(value, onApply, timeZone);

  return (
    <>
      {/* 宽屏：左栏 + 双月历的浮层；窄屏：胶囊 + 单月历的弹窗。两套容器各藏一套触发器。 */}
      <span className="hidden md:inline-flex">
        <s-button commandFor={popoverId} icon="calendar">
          {picker.triggerLabel}
        </s-button>
      </span>
      <span className="md:hidden">
        <s-button commandFor={modalId} icon="calendar">
          {picker.triggerLabel}
        </s-button>
      </span>
      <s-popover id={popoverId} inlineSize="720px" onShow={picker.reset} onHide={picker.reset}>
        <DesktopPicker state={picker} closeId={popoverId} />
      </s-popover>
      <s-modal id={modalId} heading={t("DateRange")} onShow={picker.reset} onHide={picker.reset}>
        <MobilePicker state={picker} closeId={modalId} />
      </s-modal>
    </>
  );
}

type PickerState = ReturnType<typeof usePickerState>;

function usePickerState(
  value: DateRange,
  onApply: (range: DateRange) => void,
  timeZone?: DateTimeZone,
) {
  const { t, i18n } = useTranslation();
  const resolvedTimeZone = resolveTimeZone(timeZone);
  const [draft, setDraft] = useState(value);
  const [viewMonth, setViewMonth] = useState(() => pickerViewMonth(value, resolvedTimeZone));
  const [picking, setPicking] = useState(false);
  const [hoverDate, setHoverDate] = useState<string>();
  const [error, setError] = useState("");
  const today = todayIso(resolvedTimeZone);
  const preset = matchPreset(draft, resolvedTimeZone);
  const appliedPreset = matchPreset(value, resolvedTimeZone);
  const triggerLabel =
    appliedPreset === "custom"
      ? `${value.start} → ${value.end}`
      : t(DATE_PRESETS.find((item) => item.id === appliedPreset)?.labelKey ?? "Last30Days");
  const displayRange = resolvePickerRange({ range: draft, hoverDate, picking });
  const valid = isValidDateRange(draft, resolvedTimeZone);
  const currentMonth = monthKey(today);
  const weekdays = useMemo(() => weekdayLabels(i18n.language), [i18n.language]);

  const selectPreset = (next: Exclude<DatePreset, "custom">) => {
    const range = rangeForPreset(next, resolvedTimeZone);
    setDraft(range);
    setViewMonth(pickerViewMonth(range, resolvedTimeZone));
    setPicking(false);
    setHoverDate(undefined);
    setError("");
  };

  const selectDay = (date: string) => {
    if (isDateAfterToday(date, resolvedTimeZone)) return;
    setError("");
    if (!picking) {
      setDraft({ start: date, end: date });
      setPicking(true);
      return;
    }
    setDraft(clampDateRange(draft.start, date));
    setPicking(false);
    setHoverDate(undefined);
  };

  const updateField = (key: "start" | "end", next: string) => {
    if (!isIsoDate(next)) return;
    const range = { ...draft, [key]: next };
    setDraft(range);
    setViewMonth(pickerViewMonth(range, resolvedTimeZone));
    setPicking(false);
    setHoverDate(undefined);
    setError("");
  };

  const reset = () => {
    setDraft(value);
    setViewMonth(pickerViewMonth(value, resolvedTimeZone));
    setPicking(false);
    setHoverDate(undefined);
    setError("");
  };

  const save = () => {
    if (!valid) {
      setError(t("ChooseAStartDateOnOr"));
      return;
    }
    onApply(draft);
  };

  return {
    draft,
    preset,
    triggerLabel,
    displayRange,
    viewMonth,
    currentMonth,
    today,
    timeZone: resolvedTimeZone,
    locale: i18n.language,
    weekdays,
    error,
    valid,
    selectPreset,
    selectDay,
    updateField,
    setHoverDate,
    shiftViewMonth: (amount: number) => setViewMonth(shiftMonth(viewMonth, amount)),
    reset,
    save,
  };
}

function DesktopPicker({ state, closeId }: { state: PickerState; closeId: string }) {
  const { t } = useTranslation();
  const {
    draft,
    preset,
    displayRange,
    viewMonth,
    currentMonth,
    today,
    timeZone,
    locale,
    weekdays,
    error,
    valid,
    selectPreset,
    selectDay,
    updateField,
    setHoverDate,
    shiftViewMonth,
    save,
  } = state;

  return (
    <div className="flex min-w-160 bg-white">
      <div className="flex w-42 shrink-0 flex-col gap-0.5 border-r border-[#e3e3e3] p-2">
        {DATE_PRESETS.map((item) => {
          const selected = preset === item.id;
          return (
            <button
              key={item.id}
              type="button"
              className={`flex w-full cursor-pointer items-center justify-between rounded-lg border-0 px-3 py-2 text-left ${
                selected ? "bg-[#f1f1f1]" : "bg-transparent hover:bg-[#f7f7f7]"
              }`}
              aria-pressed={selected}
              onClick={() => selectPreset(item.id)}
            >
              <Text as="span" variant="bodyMd">
                {t(item.labelKey)}
              </Text>
              {selected ? <s-icon type="check" /> : null}
            </button>
          );
        })}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3 p-3">
        <s-grid gridTemplateColumns="1fr auto 1fr" gap="small-200" alignItems="center">
          <s-text-field
            label={t("Start")}
            labelAccessibilityVisibility="exclusive"
            icon="calendar"
            value={draft.start}
            onChange={(event) => updateField("start", event.currentTarget.value)}
          />
          <s-icon type="arrow-right" />
          <s-text-field
            label={t("End")}
            labelAccessibilityVisibility="exclusive"
            icon="calendar"
            value={draft.end}
            onChange={(event) => updateField("end", event.currentTarget.value)}
          />
        </s-grid>
        <div className="grid grid-cols-2 gap-4" onMouseLeave={() => setHoverDate(undefined)}>
          <MonthCalendar
            month={viewMonth}
            locale={locale}
            weekdays={weekdays}
            range={displayRange}
            today={today}
            timeZone={timeZone}
            nav="previous"
            navDisabled={false}
            previousLabel={t("PreviousMonth")}
            nextLabel={t("NextMonth")}
            onNavigate={shiftViewMonth}
            onSelect={selectDay}
            onHover={setHoverDate}
          />
          <MonthCalendar
            month={shiftMonth(viewMonth, 1)}
            locale={locale}
            weekdays={weekdays}
            range={displayRange}
            today={today}
            timeZone={timeZone}
            nav="next"
            navDisabled={viewMonth >= currentMonth}
            previousLabel={t("PreviousMonth")}
            nextLabel={t("NextMonth")}
            onNavigate={shiftViewMonth}
            onSelect={selectDay}
            onHover={setHoverDate}
          />
        </div>
        {error ? (
          <s-banner tone="warning">
            <Text as="p" variant="bodyMd">
              {error}
            </Text>
          </s-banner>
        ) : null}
        <s-stack direction="inline" gap="small-200" justifyContent="end">
          <s-button commandFor={closeId} command="--hide">
            {t("Cancel")}
          </s-button>
          <s-button
            variant="primary"
            {...(valid ? { commandFor: closeId, command: "--hide" as const } : {})}
            onClick={save}
          >
            {t("Save")}
          </s-button>
        </s-stack>
      </div>
    </div>
  );
}

function MobilePicker({ state, closeId }: { state: PickerState; closeId: string }) {
  const { t } = useTranslation();
  const {
    draft,
    preset,
    displayRange,
    viewMonth,
    currentMonth,
    today,
    timeZone,
    locale,
    weekdays,
    error,
    valid,
    selectPreset,
    selectDay,
    setHoverDate,
    shiftViewMonth,
    save,
  } = state;

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {DATE_PRESETS.map((item) => {
            const selected = preset === item.id;
            return (
              <button
                key={item.id}
                type="button"
                className={`cursor-pointer rounded-full border-0 px-3 py-1.5 text-[13px] leading-5 whitespace-nowrap ${
                  selected ? "bg-[#e3e3e3]" : "bg-[#f1f1f1] hover:bg-[#e3e3e3]"
                }`}
                aria-pressed={selected}
                onClick={() => selectPreset(item.id)}
              >
                {t(item.labelKey)}
              </button>
            );
          })}
        </div>
        <MonthCalendar
          month={viewMonth}
          locale={locale}
          weekdays={weekdays}
          range={displayRange}
          today={today}
          timeZone={timeZone}
          nav="both"
          navDisabled={viewMonth >= currentMonth}
          previousLabel={t("PreviousMonth")}
          nextLabel={t("NextMonth")}
          onNavigate={shiftViewMonth}
          onSelect={selectDay}
          onHover={setHoverDate}
        />
        <Text as="p" variant="bodyMd" tone="subdued">
          {t("SelectedRange", { range: `${draft.start} – ${draft.end}` })}
        </Text>
        {error ? (
          <s-banner tone="warning">
            <Text as="p" variant="bodyMd">
              {error}
            </Text>
          </s-banner>
        ) : null}
      </div>
      <s-button slot="secondary-actions" commandFor={closeId} command="--hide">
        {t("Cancel")}
      </s-button>
      <s-button
        slot="primary-action"
        variant="primary"
        {...(valid ? { commandFor: closeId, command: "--hide" as const } : {})}
        onClick={save}
      >
        {t("Save")}
      </s-button>
    </>
  );
}

function weekdayLabels(locale: string) {
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(Date.UTC(2024, 0, 7 + index));
    const label = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(
      date,
    );
    return locale.toLowerCase().startsWith("en") ? label.slice(0, 2) : label;
  });
}

function monthTitle(month: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${month}-01T00:00:00Z`));
}

function rangeBarStyle(role: ReturnType<typeof calendarDayRole>) {
  if (role === "inRange") return { background: "#f1f1f1" };
  if (role === "start") {
    return { background: "linear-gradient(to right, transparent 50%, #f1f1f1 50%)" };
  }
  if (role === "end") {
    return { background: "linear-gradient(to left, transparent 50%, #f1f1f1 50%)" };
  }
  return undefined;
}

function MonthCalendar({
  month,
  locale,
  weekdays,
  range,
  today,
  timeZone,
  nav,
  navDisabled,
  previousLabel,
  nextLabel,
  onNavigate,
  onSelect,
  onHover,
}: {
  month: string;
  locale: string;
  weekdays: string[];
  range: DateRange;
  today: string;
  timeZone: DateTimeZone;
  nav: "previous" | "next" | "both";
  navDisabled: boolean;
  previousLabel: string;
  nextLabel: string;
  onNavigate: (amount: number) => void;
  onSelect: (date: string) => void;
  onHover: (date?: string) => void;
}) {
  const navButton = (direction: "previous" | "next") => (
    <button
      type="button"
      className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent hover:bg-[#f1f1f1] disabled:cursor-default disabled:opacity-40"
      aria-label={direction === "previous" ? previousLabel : nextLabel}
      // 只有“下个月”会被禁用：不能翻到未来的月份。
      disabled={direction === "next" && navDisabled}
      onClick={() => onNavigate(direction === "previous" ? -1 : 1)}
    >
      <s-icon type={direction === "previous" ? "chevron-left" : "chevron-right"} />
    </button>
  );

  return (
    <div className="min-w-0">
      <div className="mb-2 grid grid-cols-[28px_1fr_28px] items-center">
        {nav !== "next" ? navButton("previous") : <span />}
        <Text as="span" variant="bodyMd" fontWeight="semibold" alignment="center">
          {monthTitle(month, locale)}
        </Text>
        {nav !== "previous" ? navButton("next") : <span />}
      </div>
      <div className="grid grid-cols-7">
        {weekdays.map((label, index) => (
          <Text
            key={`${month}-weekday-${index}`}
            as="span"
            variant="bodySm"
            tone="subdued"
            alignment="center"
          >
            {label}
          </Text>
        ))}
        {calendarCells(month).map((date, index) => {
          if (!date) return <div key={`${month}-empty-${index}`} className="h-8" />;
          const role = calendarDayRole(date, range);
          const disabled = isDateAfterToday(date, timeZone);
          const selected = role === "start" || role === "end" || role === "single";
          return (
            <button
              key={date}
              type="button"
              className={`relative flex h-8 items-center justify-center border-0 bg-transparent p-0 ${
                disabled ? "cursor-default" : "cursor-pointer"
              }`}
              style={rangeBarStyle(role)}
              aria-label={date}
              aria-pressed={selected}
              aria-current={date === today ? "date" : undefined}
              disabled={disabled}
              onMouseEnter={() => {
                if (!disabled) onHover(date);
              }}
              onFocus={() => {
                if (!disabled) onHover(date);
              }}
              onClick={() => onSelect(date)}
            >
              <span
                className={`relative z-10 flex h-7 w-7 items-center justify-center rounded-full text-[13px] leading-none ${
                  selected
                    ? "bg-[#303030] text-white"
                    : disabled
                      ? "text-[#8a8a8a]"
                      : date === today
                        ? "text-[#303030] shadow-[inset_0_0_0_1px_#303030] hover:bg-[#f1f1f1]"
                        : "text-[#303030] hover:bg-[#f1f1f1]"
                }`}
              >
                {Number(date.slice(8))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
